import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import { Box, Button, Container, Grid, Paper, Typography } from "@mui/material";
import { DateRange } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { formatDate, getDateFromParam } from "utils/date";
import { WateringsContent } from "./components/WateringsContent";

const StyledContainer = styled(Container)(({ theme }) => ({
  flexGrow: 1,
  marginTop: theme.spacing(4),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const StyledIcon = styled(DateRange)(({ theme }) => ({
  verticalAlign: "middle",
  marginRight: theme.spacing(1),
}));

const UserWateringsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams<{ userId: string }>();

  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);

  useEffect(() => {
    if (!userId) navigate("/reports");
  }, [userId, navigate]);

  const queryDates = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const start = params.get("start");
    const end = params.get("end");
    if (start && end) {
      return { start, end };
    }
    return null;
  }, [location.search]);

  const handleSubmit = () => {
    if (selectedStartDate && selectedEndDate) {
      const startDate = formatDate(selectedStartDate);
      const endDate = formatDate(selectedEndDate);
      navigate(`/reports/${userId}?start=${startDate}&end=${endDate}`);
    }
  };

  useEffect(() => {
    if (queryDates?.start && queryDates.end) {
      setSelectedStartDate(getDateFromParam(queryDates.start));
      setSelectedEndDate(getDateFromParam(queryDates.end));
    }
  }, [queryDates]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledContainer>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StyledPaper>
              <Box display={"flex"} alignItems={"center"} justifyContent={"center"}>
                <StyledIcon />
                <Typography variant="h5" gutterBottom>
                  Select Watering Date Range
                </Typography>
              </Box>
              <Grid container spacing={3} justifyContent="center">
                <Grid item>
                  <DatePicker
                    label="Start Date"
                    value={selectedStartDate}
                    onChange={(date) => setSelectedStartDate(date)}
                    maxDate={selectedEndDate}
                    slotProps={{
                      textField: {
                        margin: "normal",
                        id: "start-date-picker",
                      },
                    }}
                  />
                </Grid>
                <Grid item>
                  <DatePicker
                    label="End Date"
                    value={selectedEndDate}
                    onChange={(date) => setSelectedEndDate(date)}
                    minDate={selectedStartDate}
                    slotProps={{
                      textField: {
                        margin: "normal",
                        id: "end-date-picker",
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <StyledButton
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={!selectedStartDate || !selectedEndDate}
              >
                Submit
              </StyledButton>
            </StyledPaper>
          </Grid>
          <Grid item xs={12}>
            <WateringsContent queryDates={queryDates} userId={userId} />
          </Grid>
        </Grid>
      </StyledContainer>
    </LocalizationProvider>
  );
};

export default UserWateringsPage;
